import type { ReactNode } from 'react';
import { useApi, Cargando, ErrorApi, formatU, formatFecha } from '../shared/ui.tsx';

interface PagoTurno {
  id: string;
  fecha: string;
  categoria_nombre: string;
  horas_normales: number;
  horas_nocturnas: number;
  pago_calculado: number;
  ajuste_manual: number | null;
  motivo_ajuste: string | null;
}
interface PagoLiq {
  id: string;
  periodo_desde: string;
  periodo_hasta: string;
  total: number;
  estado: string;
  pagada_en: string | null;
}

export function MisPagosPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<{ turnos: PagoTurno[]; liquidaciones: PagoLiq[] }>('/api/personal/pagos');

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;
  if (!datos) return null;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-neutro-800">Mis pagos</h2>

      <section className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
        <h3 className="font-semibold text-neutro-800">Turnos trabajados</h3>
        {datos.turnos.length === 0 ? (
          <p className="mt-2 text-sm text-neutro-400">Sin turnos cargados todavía.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {datos.turnos.map((t) => {
              const total = t.pago_calculado + (t.ajuste_manual ?? 0);
              return (
                <li key={t.id} className="flex items-center justify-between border-b border-contorno-var py-2">
                  <div>
                    <p className="font-medium">{formatFecha(t.fecha)} · {t.categoria_nombre}</p>
                    <p className="text-xs text-neutro-400">
                      {t.horas_normales}h normal + {t.horas_nocturnas}h noct.
                      {t.ajuste_manual !== null && <span className="text-acento-600"> · ajuste {t.ajuste_manual > 0 ? '+' : ''}{formatU(t.ajuste_manual)}</span>}
                      {t.motivo_ajuste && <span className="text-neutro-400"> ({t.motivo_ajuste})</span>}
                    </p>
                  </div>
                  <span className="font-bold text-neutro-800">{formatU(total)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
        <h3 className="font-semibold text-neutro-800">Liquidaciones</h3>
        {datos.liquidaciones.length === 0 ? (
          <p className="mt-2 text-sm text-neutro-400">Sin liquidaciones cerradas todavía.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {datos.liquidaciones.map((l) => {
              const color = l.estado === 'pagada' ? 'text-estado-ok' : l.estado === 'cerrada' ? 'text-estado-pendiente' : 'text-neutro-400';
              return (
                <li key={l.id} className="flex items-center justify-between border-b border-contorno-var py-2">
                  <div>
                    <p className="font-medium">{formatFecha(l.periodo_desde)} – {formatFecha(l.periodo_hasta)}</p>
                    <p className={`text-xs font-medium ${color}`}>
                      {l.estado}{l.pagada_en ? ` · pagada ${formatFecha(l.pagada_en)}` : ''}
                    </p>
                  </div>
                  <span className="font-bold text-neutro-800">{formatU(l.total)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}